import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  Pagination,
  Button,
  SimpleTable,
} from '../../../design-system';
import { DashboardHeader } from '../../dashboard';
import { usePaginationState, useYardCompaniesQuery } from '../../../hooks';
import { formatPhoneNumber } from '../../../utils';
import { TABLE } from '../../../const';
import { i18n } from '../../../i18n';

interface YardCompaniesProps {
  innerScroll?: boolean;
}

export const YardCompaniesPage = styled.div<YardCompaniesProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const YardCompaniesHeader = ({ totalCount }: any) => {
  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.yardCompanies.title', { totalCount })}
      </Typography>
      <Button size={'medium'} to={'/dashboard/yardcompanies/add'}>
        {i18n.t('button.addYardCompany')}
      </Button>
    </DashboardHeader>
  );
};

export const YardCompaniesBody = ({ setTotalCount }: any) => {
  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const { data = { data: [], totalCount: 0 }, isFetching } = useYardCompaniesQuery({
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  const handleRowClick = (to: string) => {
    navigate(to);
  };

  return (
    <SimpleTable
      data={data}
      tableRowProps={(row, i) => ({
        key: 'yard-companies' + i,
        onClick: () => handleRowClick(`/dashboard/yardcompanies/${row.id}`),
      })}
      columns={[
        {
          headText: () => i18n.t('label.name'),
          body: (row) => (
            <TableCell component={'th'} align={'left'}>
              {row.legalName}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.email'),
          bodyText: (row) => row.email,
        },
        {
          headText: () => i18n.t('label.phoneNumber'),
          bodyText: (row) => formatPhoneNumber(row.phoneNumber),
        },
        {
          headText: () => i18n.t('label.country'),
          bodyText: (row) => row.address?.country,
        },
        {
          headText: () => i18n.t('label.city'),
          bodyText: (row) => row.address?.city,
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handlePageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
